import { useCallback, useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha' // eslint-disable-line import/no-named-as-default
import { connect } from 'react-redux'
import { NavigateFunction, useNavigate } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'
import Cookies from 'js-cookie'
import styled from 'styled-components'

import { ClaimDetailsType } from '../claim/types'
import { buildHeaders } from '../lib/buildHeaders'
import { LogoService } from '../logo/logo-service'
import { ClaimDataLogoRequest } from '../logo/types'
import { addNotification as addNotificationAction } from '../notifications/notifications-actions'
import type { NotificationType } from '../notifications/types'
import { getReCaptchaKey } from '../shared/constants'
import { PrimaryButton } from '../shared/styledComponents'
import { TrackingService } from '../tracking/tracking-service'
import type { ReqTrackingType } from '../tracking/types'
import type { GlobalState } from '../types'
import { getUploadMeta } from './getUploadMeta'
import type {
  FileType,
  GetUploadMetaProps,
  Metadata,
  TrackingRecordType,
  UploadIdentityType
} from './types'
import { onTrackingError, showAllCategories, uploadRequest } from './upload-actions'
import { cookiesToKeep } from './upload-constants'
import { getClaimHandlers, getDisableSubmitStatus, getIdentityValues } from './upload-selectors'

const SubmitButton = styled(PrimaryButton)`
  margin: 0 auto;
  float: none;
  width: 37%;
  color: '#FFFFFF';
  background-color: ${props => props.disabled ? '#b5b6b8' : '#CC2D30'};
  @media (max-width: 468px) {
    width: 75%;
    margin: 0 auto;
    float: none;
  }
`
const SubmitContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

type UploadSubmitProps = {
  showAllTheCategories(): void
  files: FileType[]
  claimDetails: GetUploadMetaProps['claimDetails']
  onUpload(
    navigate: NavigateFunction,
    files: FileType[],
    meta: Metadata,
    trackingInfo?: TrackingRecordType
  ): void
  identity: GetUploadMetaProps['identity']
  sourceSystem: string
  handler: string
  claimHandlers: GetUploadMetaProps['claimHandlers']
  handleTrackingError(arg: boolean): void
  disableSubmit: boolean
  addNotification(notification: NotificationType): void
}

const UploadSubmitComponent = (props: UploadSubmitProps) => {
  const {
    showAllTheCategories,
    files,
    claimDetails,
    onUpload,
    identity,
    sourceSystem,
    handler,
    claimHandlers,
    handleTrackingError,
    disableSubmit,
    addNotification
  } = props
  const [recaptchaSolved, setRecaptchaSolved] = useState<boolean>(false)
  const refRecaptcha = useRef<ReCAPTCHA>(null)
  const refIsFirstRun = useRef(true)
  const [cookieCache, setCookieCache] = useState<{name: string; value: string}[]>([])
  const navigate = useNavigate()

  // updated from componentDidUpdate
  useEffect(() => {
    if (refRecaptcha.current && !disableSubmit){
      if (!refIsFirstRun.current) {
        refRecaptcha.current.reset()
      } else {
        refIsFirstRun.current = false
      }
    }
  }, [disableSubmit])

  const handleSubmit = useCallback(() => {
    refRecaptcha.current?.execute()
  }, [])

  const sendRequest = useCallback(async (recaptchaToken: string) => {
    showAllTheCategories()

    const meta = getUploadMeta({
      claimDetails, identity, sourceSystem, claimHandlers, handler
    })

    try {
      const trackingInfo = await getTrackingInfo(claimDetails, files, identity, recaptchaToken)

      const tooManyCookies = document.cookie.length >= 19456 // 19KiB
      // Max allowable header size is 20KB
      // XHR requests do not allow us to omit cookies from headers on same-origin requests.
      // If the size of cookies is >= 19KiB, we need to clean up some of the cookies that have
      // accumulated. In order to minimize disruption, certain cookies are exempt from deletion and
      // can be found in upload-constants.ts, but in order for the request to be successful, we
      // need to reduce the http header size by deleting cookies. Cookies are restored on
      // component unmount.
      if (tooManyCookies) {
        const cookies: Record<string, string> = Cookies.get()
        const cookiesToCache: { name: string; value: string }[] = []
        for (let i = 0; i < Object.keys(cookies).length; i++) {
          const name = Object.keys(cookies)[i]
          const value = cookies[name]
          // Protect certain cookies from deletion
          if (cookiesToKeep.includes(name)) continue
          cookiesToCache.push({ name, value })
          // Deletes .travelers.com cookies by setting expiration into the past.
          Cookies.remove(name, { path: '/', domain: '.travelers.com' })
          // Deletes cookies on this subdomain.
          Cookies.remove(name, { path: '/', domain: window.location.hostname })
        }
        setCookieCache(cookiesToCache)
      }
      onUpload(navigate, files, meta, trackingInfo)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown Error'
      console.log('tracking error: ' + message)
      handleTrackingError(true)
      onUpload(navigate, files, meta)
    }
  }, [
    claimDetails,
    identity,
    sourceSystem,
    claimHandlers,
    handler,
    files,
    handleTrackingError,
    onUpload,
    setCookieCache,
    showAllTheCategories,
    navigate
  ])

  // If cookies were deleted due to large size, then they get restored here.
  useEffect(() => {
    return () => {
      if (cookieCache.length){
        for (let i = 0; i < cookieCache.length; i++) {
          const cookie = cookieCache[i]
          Cookies.set(cookie.name, cookie.value, { path: '/', domain: '.travelers.com' })
        }
        setCookieCache([])
      }
    }
  }, [cookieCache, setCookieCache])

  const onRecaptchaResolved = useCallback(() => {
    if (recaptchaSolved) {
      // We've seen in lower environments that the recaptcha token can be resolved multiple times
      // when the submit button is clicked. This early return prevents multiple requests or false
      // errors.
      return
    }
    const recaptchaToken = refRecaptcha.current?.getValue()
    if (recaptchaToken) {
      setRecaptchaSolved(true)
      sendRequest(recaptchaToken)
    } else {
      addNotification({
        name: 'uploadError',
        body: 'We are unable to process your request. Please contact your Claim professional.',
        severity: 'error',
        title: 'Upload Error'
      })
    }
  }, [sendRequest, addNotification, recaptchaSolved])


  const captchaKey = getReCaptchaKey(window.location.hostname)

  return (
    <SubmitContainer>
      <SubmitButton
        id='submit-files'
        onClick={handleSubmit}
        disabled={disableSubmit}
      >
        Submit Files
      </SubmitButton>
      <ReCAPTCHA
        id='uploadSubmitRecaptcha'
        size='invisible'
        ref={refRecaptcha}
        sitekey={captchaKey}
        onChange={onRecaptchaResolved}
      />
    </SubmitContainer>
  )
}

const getTrackingInfo = async (
  claimDetails: ClaimDetailsType,
  files: FileType[],
  identity: UploadIdentityType,
  recaptchaToken: string
) => {
  const trackingService = TrackingService.getInstance()
  const logoService = LogoService.getInstance()
  const filesList: ReqTrackingType['files'] = []
  files.map((f: FileType) => filesList.push({
    fileName: f.file.name,
    fileSize: f.file.size
  }))

  const claimDataLogoRequest: ClaimDataLogoRequest = {
    authorPersId: 'EAV',
    loggedInAfo: claimDetails.afo ?? '',
    lob: claimDetails.lob,
    claimant: claimDetails.lob === 'W' ? '001' : '000',
    claim: claimDetails.claim
  }

  const logoInfo = await logoService?.getLogo(claimDataLogoRequest)

  const reqTracking: ReqTrackingType = {
    'claimNumber': claimDetails.claim,
    'claimantNumber': claimDetails.lob === 'W' ? '001' : '000',
    'thirdPartyEmail': identity.email,
    'thirdPartyName': identity.name,
    'claimAfo': claimDetails.afo ?? '',
    'systemSource': claimDetails.systemSource ?? '',
    'role': identity.role,
    'files': filesList,
    'logoCode': (logoInfo && logoInfo.logoCode) ? logoInfo.logoCode : '',
    'underwriterName': (logoInfo && logoInfo.underwriterName) ? logoInfo.underwriterName : ''
  }

  const headers = buildHeaders({
    'Content-Type': 'application/json',
    recaptchaToken: recaptchaToken
  })

  return trackingService?.getTrackingInfo(reqTracking, headers)
}

const mapStateToProps = (state: GlobalState) => ({
  files: state.upload.files,
  category: state.upload.category,
  disableSubmit: getDisableSubmitStatus(state),
  claimDetails: state.claim.claimDetails,
  identity: getIdentityValues(state),
  sourceSystem: state.upload.sourceSystem,
  handler: state.upload.handler,
  claimHandlers: getClaimHandlers(state),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  handleTrackingError: (trackingError: boolean) => dispatch(onTrackingError(trackingError)),
  onUpload: (
    navigate: NavigateFunction,
    files: FileType[],
    meta: Metadata,
    trackingInfo: TrackingRecordType
  ) => dispatch(uploadRequest(navigate, files, meta, trackingInfo)),
  showAllTheCategories: () => dispatch(showAllCategories()),
  addNotification: (notification: NotificationType) => dispatch(addNotificationAction(notification))
})

const UploadSubmit =
  connect(mapStateToProps, mapDispatchToProps)(UploadSubmitComponent)

export {
  UploadSubmit,
  UploadSubmitComponent // for testing without core HOC
}
